import Layout from '@/layout'

/**
 * 科研管理 v2 一级路由
 * funcPerms 与 docs/菜单SQL_科研_信息日常_v2增量.sql 菜单权限一致（v2 去掉「科研管理」目录，全部为一级菜单）
 * 每个一级菜单 = 独立 Layout 包裹；页面组件在 children 空路径下渲染（保留侧边栏/顶栏框架）
 * 课题详情为隐藏路由（research:project:view），activeMenu 指向课题列表，保持菜单高亮
 */
export const researchRoutes = [
  {
    // 门户科研卡片 href 指向 /research（sys_base_url）；隐藏的入口重定向，避免 404。科研管理为系统标题，不显示为菜单
    path: '/research',
    component: Layout,
    redirect: '/research/project/list',
    hidden: true,
    meta: { title: '科研管理', affix: false, noCache: true },
    children: []
  },
  {
    // 我的课题：父菜单，下挂 我的课题列表/课题阶段/课题任务（子菜单复用 research:project:list，无需新权限）
    path: '/research/project',
    component: Layout,
    redirect: '/research/project/list',
    funcPerms: 'research:project:list',
    meta: { title: '我的课题', icon: 'list', affix: false, noCache: true },
    children: [
      {
        path: 'list',
        component: () => import('@/views/research/project/list'),
        funcPerms: 'research:project:list',
        name: 'ResearchProjectList',
        meta: { title: '我的课题列表', icon: 'list', affix: false, noCache: true }
      },
      {
        path: 'phase',
        component: () => import('@/views/research/phase/list'),
        funcPerms: 'research:project:list',
        name: 'ResearchPhaseList',
        meta: { title: '课题阶段', icon: 'list', affix: false, noCache: true }
      },
      {
        path: 'task',
        component: () => import('@/views/research/task/list'),
        funcPerms: 'research:project:list',
        name: 'ResearchTaskList',
        meta: { title: '课题任务', icon: 'list', affix: false, noCache: true }
      }
    ]
  },
  {
    path: '/research/project/detail/:id',
    component: Layout,
    hidden: true,
    funcPerms: 'research:project:view',
    meta: { title: '课题详情', activeMenu: '/research/project/list', affix: false, noCache: true },
    children: [
      {
        path: '',
        component: () => import('@/views/research/project/detail'),
        name: 'ResearchProjectDetail',
        meta: { title: '课题详情', activeMenu: '/research/project/list', affix: false, noCache: true }
      }
    ]
  },
  {
    path: '/research/project/apply',
    component: Layout,
    hidden: true,
    funcPerms: 'research:project:list',
    meta: { title: '课题申请', activeMenu: '/research/project/list', affix: false, noCache: true },
    children: [
      {
        path: '',
        component: () => import('@/views/research/project/apply'),
        name: 'ResearchProjectApply',
        meta: { title: '课题申请', activeMenu: '/research/project/list', affix: false, noCache: true }
      }
    ]
  },
  {
    path: '/research/phase/:projectId',
    component: Layout,
    hidden: true,
    funcPerms: 'research:project:list',
    meta: { title: '阶段拆分', activeMenu: '/research/project/list', affix: false, noCache: true },
    children: [
      {
        path: '',
        component: () => import('@/views/research/phase'),
        name: 'ResearchPhaseManage',
        meta: { title: '阶段拆分', activeMenu: '/research/project/list', affix: false, noCache: true }
      }
    ]
  },
  {
    path: '/research/task/phase/:phaseId',
    component: Layout,
    hidden: true,
    funcPerms: 'research:project:list',
    meta: { title: '任务拆分', activeMenu: '/research/project/list', affix: false, noCache: true },
    children: [
      {
        path: '',
        component: () => import('@/views/research/task'),
        name: 'ResearchTaskSplit',
        meta: { title: '任务拆分', activeMenu: '/research/project/list', affix: false, noCache: true }
      }
    ]
  },
  {
    // 待办任务：审批任务 + 我的执行任务 上级菜单（父级不设 funcPerms，子项各自按权限过滤）
    path: '/research/todo',
    component: Layout,
    redirect: '/research/todo/approval',
    alwaysShow: true, // 只有一个子菜单时也显示父菜单
    meta: { title: '待办任务', icon: 'list', affix: false, noCache: true },
    children: [
      {
        path: 'approval',
        component: () => import('@/views/research/approval'),
        funcPerms: 'research:project:approve',
        name: 'ResearchApprovalList',
        meta: { title: '审批任务', icon: 'list', affix: false, noCache: true }
      },
      {
        path: 'mine',
        component: () => import('@/views/research/task/mine/exec'),
        funcPerms: 'research:project:mytask',
        name: 'ResearchTaskMineExec',
        meta: { title: '我的执行任务', icon: 'list', affix: false, noCache: true }
      }
    ]
  },
  {
    path: '/research/midterm',
    component: Layout,
    funcPerms: 'research:project:midterm',
    meta: { title: '中期', icon: 'list', affix: false, noCache: true },
    children: [
      {
        path: '',
        component: () => import('@/views/research/midterm'),
        name: 'ResearchMidterm',
        meta: { title: '中期', icon: 'list', affix: false, noCache: true }
      }
    ]
  },
  {
    path: '/research/finish',
    component: Layout,
    funcPerms: 'research:project:finish',
    meta: { title: '结题', icon: 'list', affix: false, noCache: true },
    children: [
      {
        path: '',
        component: () => import('@/views/research/finish'),
        name: 'ResearchFinish',
        meta: { title: '结题', icon: 'list', affix: false, noCache: true }
      }
    ]
  },
  {
    path: '/research/result',
    component: Layout,
    funcPerms: 'research:project:result',
    meta: { title: '结果汇总', icon: 'list', affix: false, noCache: true },
    children: [
      {
        path: '',
        component: () => import('@/views/research/result'),
        name: 'ResearchResult',
        meta: { title: '结果汇总', icon: 'list', affix: false, noCache: true }
      }
    ]
  },
  {
    path: '/research/project/all',
    component: Layout,
    funcPerms: 'research:project:viewAll',
    meta: { title: '课题列表', icon: 'list', affix: false, noCache: true },
    children: [
      {
        path: '',
        component: () => import('@/views/research/project/all'),
        name: 'ResearchProjectAll',
        meta: { title: '课题列表', icon: 'list', affix: false, noCache: true }
      }
    ]
  }
]
